import {isValid} from 'date-fns';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {SearchTestDriveCalendarApiArg} from '@dms/api/sales';

import {getApiDateString, parseDate, useDebouncedValue, useQueryState} from 'shared/web';

import {TEST_DRIVE_CALENDAR} from '../constants/TEST_DRIVE_CALENDAR';
import {getTestDriveCalendarWindow} from '../utils/getTestDriveCalendarWindow';

export const PARAM = {
  rangeMode: 'rangeMode',
  selectedDate: 'selectedDate',
  search: 'search',
  page: 'page',
  size: 'size',
} as const;

const RADIX = 10;

const toPositiveInteger = (value: string | null, fallback: number) => {
  const parsed = Number.parseInt(value ?? '', RADIX);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/** `toISOString` throws on an invalid date, so a stale URL value has to degrade to today. */
const toSelectedDate = (value: string | null) => {
  const parsed = parseDate(value);

  return isNotNil(parsed) && isValid(parsed)
    ? getApiDateString(parsed)
    : getApiDateString(new Date());
};

export const useTestDriveCalendarControl = () => {
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const [rangeModeParam, setRangeModeParam] = useQueryState(PARAM.rangeMode);
  const [selectedDateParam, setSelectedDateParam] = useQueryState(PARAM.selectedDate);
  // Replaces rather than pushes, so Back does not step through every keystroke. The page
  // parameter is bound twice because pagination itself still pushes.
  const [searchParam, setSearchParam, clearSearchParam] = useQueryState(PARAM.search, true);
  const [pageParam, setPageParam, clearPageParam] = useQueryState(PARAM.page);
  const [, , clearPageParamReplacing] = useQueryState(PARAM.page, true);
  const [sizeParam, setSizeParam] = useQueryState(PARAM.size);

  const assignedBranches = currentUser?.branches ?? [];

  const rangeMode =
    rangeModeParam === String(TEST_DRIVE_CALENDAR.RANGE_DAYS.MONTH)
      ? TEST_DRIVE_CALENDAR.RANGE_DAYS.MONTH
      : TEST_DRIVE_CALENDAR.RANGE_DAYS.WEEK;

  const selectedDate = toSelectedDate(selectedDateParam);

  const search = searchParam;
  const debouncedSearch = useDebouncedValue(search, TEST_DRIVE_CALENDAR.SEARCH_DEBOUNCE_MS);
  const page = toPositiveInteger(pageParam, TEST_DRIVE_CALENDAR.DEFAULT_PAGE);
  const sizeFromUrl = toPositiveInteger(sizeParam, TEST_DRIVE_CALENDAR.DEFAULT_PAGE_SIZE);
  const size = TEST_DRIVE_CALENDAR.PAGE_SIZES.some((allowed) => allowed === sizeFromUrl)
    ? sizeFromUrl
    : TEST_DRIVE_CALENDAR.DEFAULT_PAGE_SIZE;

  const window = getTestDriveCalendarWindow(selectedDate, rangeMode);

  const hasAssignedBranch = isNotNilOrEmpty(assignedBranches);

  const searchArg: SearchTestDriveCalendarApiArg | undefined = hasAssignedBranch
    ? {from: window.from, to: window.to, search: debouncedSearch, pagination: {page, size}}
    : undefined;

  return {
    rangeMode,
    selectedDate,
    assignedBranches,
    search,
    page,
    size,
    windowStart: window.from,
    hasAssignedBranch,
    searchArg,

    // Time navigation must not touch the page.
    setRangeMode: (value: number) => setRangeModeParam(String(value)),
    setSelectedDate: setSelectedDateParam,

    setSearch: (value: string | null) => {
      if (isNotNil(value) && value !== '') {
        setSearchParam(value);
      } else {
        clearSearchParam();
      }
      clearPageParamReplacing();
    },
    setSize: (value: number) => {
      setSizeParam(String(value));
      clearPageParam();
    },

    setPage: (value: number) => setPageParam(String(value)),
  };
};
